<template>
  <component
    :is="props.useClientLayout ? AdminLayout : PortalLayout"
    section="Reports"
    :title="user.isTenantAdmin ? 'Tenant' : 'Property' + 'Property Roster Report'"
    hideHeader
  >
    <div class="pb-4">
      <BackButton to=".."> Back to Reports List </BackButton>
    </div>
    <h1 class="py-3 mb-6 border-b flex">
      <div class="text-xl">
        <div class="font-bold">{{ user.isTenantAdmin ? "Tenant" : "Property" }} Roster Report</div>
        <div>
          {{ user.isTenantAdmin ? user.tenantName : property?.name }}
        </div>
      </div>
    </h1>
    <Loading v-if="isPropertyLoading || isReportLoading" />
    <div v-else>
      <div v-if="!reportResults || reportResults.length == 0">No building people found</div>
      <div v-else class="report-container">
        <div class="flex">
          <div class="flex flex-row gap-2 grow">
            <div v-if="!user.isTenantAdmin" class="basis-1/5">
              <BaseSelect
                defaultText="All Tenants"
                class="px-4 mb-1 text-sm bg-white"
                v-model="filteredTenant"
                :options="tenants"
              />
            </div>
            <div class="basis-1/5">
              <BaseSelect
                defaultText="All Roles"
                class="px-4 mb-1 text-sm bg-white"
                v-model="filteredRole"
                :options="siteRoles"
              />
            </div>
            <div class="basis-1/5">
              <BaseSelect
                v-if="property.floorList?.length > 0"
                defaultText="All Floors"
                class="px-4 mb-1 text-sm bg-white"
                v-model="filteredFloor"
                :options="property.floorList"
              />
            </div>
            <div v-if="!props.useClientLayout" class="basis-1/5">
              <BaseCheckbox
                label="Exclude Test Accounts"
                v-model="excludeTestAccounts"
                class="my-4"
              />
            </div>
          </div>
          <BaseButton class="float-right my-1 text-sm ml-2" @click="downloadExcel">
            Export to Excel
          </BaseButton>
        </div>
        <table class="table-auto w-full mt-4 border-2 shadow text-sm">
          <thead class="font-sm font-bold">
            <tr>
              <td class="pl-3 cursor-pointer" @click="ChangeSort(ColumnsToSort.FirstName)">
                <span class="xl:inline hidden">First </span>Name
                <UpDownArrow
                  class="float-right"
                  v-if="columnToSort == ColumnsToSort.FirstName"
                  :isUp="!isSortingDown"
                />
              </td>
              <td
                class="xl:table-cell hidden pl-3 cursor-pointer"
                @click="ChangeSort(ColumnsToSort.LastName)"
              >
                Last Name
                <UpDownArrow
                  class="float-right"
                  v-if="columnToSort == ColumnsToSort.LastName"
                  :isUp="!isSortingDown"
                />
              </td>
              <td class="pl-3 cursor-pointer" @click="ChangeSort(ColumnsToSort.Email)">
                Email
                <UpDownArrow
                  class="float-right"
                  v-if="columnToSort == ColumnsToSort.Email"
                  :isUp="!isSortingDown"
                />
              </td>
              <td
                v-if="filteredTenant == null || filteredTenant.length == 0"
                class="pl-3 cursor-pointer"
                @click="ChangeSort(ColumnsToSort.Tenant)"
              >
                Tenant
                <UpDownArrow
                  class="float-right"
                  v-if="columnToSort == ColumnsToSort.Tenant"
                  :isUp="!isSortingDown"
                />
              </td>
              <td
                v-if="filteredRole == null || filteredRole.length == 0"
                class="pl-3 cursor-pointer"
                @click="ChangeSort(ColumnsToSort.Role)"
              >
                Role
                <UpDownArrow
                  class="float-right"
                  v-if="columnToSort == ColumnsToSort.Role"
                  :isUp="!isSortingDown"
                />
              </td>
              <td
                v-if="filteredFloor == null || filteredFloor.length == 0"
                class="pl-3 cursor-pointer"
                @click="ChangeSort(ColumnsToSort.Floor)"
              >
                Floor
                <UpDownArrow
                  class="float-right"
                  v-if="columnToSort == ColumnsToSort.Floor"
                  :isUp="!isSortingDown"
                />
              </td>
              <td class="px-3 py-2 cursor-pointer" @click="ChangeSort(ColumnsToSort.Phone)">
                Phone
                <UpDownArrow
                  class="float-right"
                  v-if="columnToSort == ColumnsToSort.Phone"
                  :isUp="!isSortingDown"
                />
              </td>
              <td
                class="xl:table-cell hidden pl-3 cursor-pointer"
                @click="ChangeSort(ColumnsToSort.DateRegistered)"
              >
                Registered
                <UpDownArrow
                  class="float-right"
                  v-if="columnToSort == ColumnsToSort.DateRegistered"
                  :isUp="!isSortingDown"
                />
              </td>
            </tr>
          </thead>
          <tr
            v-for="(person, i) in filteredRosterResults"
            :key="i"
            class="font-xs font-mono hover:border-sky-800 border-bottom-2 hover:bg-sky-300"
            :class="i % 2 ? '' : 'bg-stone-200'"
          >
            <td class="pl-3">
              {{ person.firstName }}
              <span class="inline xl:hidden">{{ person.lastName }}</span>
            </td>
            <td class="xl:table-cell hidden pl-3">{{ person.lastName }}</td>
            <td class="pl-3">{{ person.loginEmail }}</td>
            <td v-if="filteredTenant == null || filteredTenant.length == 0" class="pl-3">
              {{ person.tenantName }}
            </td>
            <td v-if="filteredRole == null || filteredRole.length == 0" class="pl-3">
              {{ person.roleAlias }}
              <span v-if="getSiteRoleByName(person.roleAlias)?.isStaffRole" class="text-gray-500">
                (Staff)
              </span>
            </td>
            <td v-if="filteredFloor == null || filteredFloor.length == 0" class="pl-3">
              {{ person.primaryFloor }}
            </td>
            <td class="pl-3">{{ formatPhoneNumber(person.primaryPhone) }}</td>
            <td class="xl:table-cell hidden pl-3">{{ formatDate(person.dateRegistered) }}</td>
          </tr>
        </table>
        <div>
          <BaseButton class="float-right my-1 text-sm ml-2" @click="downloadExcel">
            Export to Excel
          </BaseButton>
        </div>
      </div>
    </div>
  </component>
</template>

<script setup lang="ts">
import type { IContent, IJsonSheet } from "json-as-xlsx";
import * as jsonAsXlsx from "json-as-xlsx";
import PortalLayout from "../../layouts/PortalLayout.vue";
import AdminLayout from "../../layouts/AdminLayout.vue";
import { useBldgStore } from "../../store/bldgStore";
import { RosterPersonDTO } from "../../store/generatedFetchClient";
import { useReportStore } from "../../store/reportStore";
import { compareFloors, formatDate, formatPhoneNumber } from "../../helpers/index";
import { NameValue } from "../../sharedTypes";
import { useUserStore } from "../../store/userStore";

const props = withDefaults(defineProps<{ useClientLayout?: boolean }>(), {
  useClientLayout: false,
});

const bldgStore = useBldgStore();
const userStore = useUserStore();
const reportStore = useReportStore();
var isPropertyLoading = computed(() => bldgStore.isLoading);
var isReportLoading = ref(false);

var property = computed(() => bldgStore.property);
var user = computed(() => userStore.user);
var tenants = computed<string[]>(() => property.value.tenants.map((t) => t.name));
var siteRoles = computed<NameValue[]>(() =>
  property.value.siteRoles.map(
    (sr) =>
      ({
        name: sr.roleAlias + (sr.isStaffRole ? " (Staff)" : ""),
        value: sr.siteRoleId,
      } as NameValue)
  )
);

var reportResults = computed<RosterPersonDTO[]>(() => reportStore.rosterResults);

var filteredRosterResults = computed<RosterPersonDTO[]>(() => {
  let results = reportResults.value?.slice();
  if (filteredTenant.value != null && filteredTenant.value.length > 0)
    results = results.filter((result) => result.tenantName == filteredTenant.value);
  if (filteredRole.value != null && filteredRole.value.length > 0)
    results = results.filter(
      (result) => result.roleAlias == getSiteRoleById(parseInt(filteredRole.value))?.roleAlias
    );
  if (filteredFloor.value != null && filteredFloor.value.length > 0)
    results = results.filter((result) => result.primaryFloor == filteredFloor.value);

  switch (columnToSort.value) {
    case ColumnsToSort.FirstName:
      results = results.sort((a, b) => {
        if (a.firstName == b.firstName) {
          return (a.lastName ?? "").localeCompare(b.lastName ?? "");
        }
        return (a.firstName ?? "").localeCompare(b.firstName ?? "");
      });
      break;
    case ColumnsToSort.LastName:
      results = results.sort((a, b) => {
        if (a.lastName == b.lastName) {
          return (a.firstName ?? "").localeCompare(b.firstName ?? "");
        }
        return (a.lastName ?? "").localeCompare(b.lastName ?? "");
      });
      break;
    case ColumnsToSort.Email:
      results = results.sort((a, b) => (a.loginEmail ?? "").localeCompare(b.loginEmail ?? ""));
      break;
    case ColumnsToSort.Tenant:
      results = results.sort((a, b) => (a.tenantName ?? "").localeCompare(b.tenantName ?? ""));
      break;
    case ColumnsToSort.Role:
      results = results.sort((a, b) => (a.roleAlias ?? "").localeCompare(b.roleAlias ?? ""));
      break;
    case ColumnsToSort.Floor:
      results = results.sort(
        (a, b) =>
          compareFloors(a.primaryFloor, b.primaryFloor) ||
          (a.lastName ?? "").localeCompare(b.lastName ?? "") ||
          (a.firstName ?? "").localeCompare(b.firstName ?? "")
      );
      break;
    case ColumnsToSort.Phone:
      results = results.sort((a, b) => (a.primaryPhone ?? "").localeCompare(b.primaryPhone ?? ""));
      break;
    case ColumnsToSort.DateRegistered:
      results = results.sort(
        (a, b) =>
          new Date(a.dateRegistered ?? 0).getTime() - new Date(b.dateRegistered ?? 0).getTime()
      );
      break;
  }

  if (isSortingDown.value) {
    results = results.reverse();
  }

  return results;
});

//Filters
var filteredTenant = ref<string>("");
var filteredRole = ref<string>("");
var filteredFloor = ref<string>("");

let excludeTestAccounts = ref(false);

enum ColumnsToSort {
  FirstName,
  LastName,
  Email,
  Tenant,
  Role,
  Floor,
  Phone,
  DateRegistered,
}

var columnToSort = ref<ColumnsToSort>(ColumnsToSort.FirstName);
var isSortingDown = ref<boolean>(true);

function ChangeSort(column: ColumnsToSort) {
  if (column == columnToSort.value) {
    isSortingDown.value = !isSortingDown.value;
  } else {
    columnToSort.value = column;
    isSortingDown.value = true;
  }
  //console.log(`Sort changed. Now sorting by col #${columnToSort.value} ${isSortingDown.value ? "down" : "up"}`);
}

onMounted(loadData);
watch(property, loadData);
watch(excludeTestAccounts, loadData);

async function loadData() {
  isReportLoading.value = true;
  if (property?.value?.portalId) {
    if (props.useClientLayout) excludeTestAccounts.value = true;
    await reportStore.getRosterReport(property.value.portalId, excludeTestAccounts.value);
  }

  if (user.value.isTenantAdmin) {
    filteredTenant.value = user.value.tenantName;
  }

  isReportLoading.value = false;
}

function downloadExcel() {
  // Extract the xlsx function from the namespace import
  const xlsx = (jsonAsXlsx as any).default || (jsonAsXlsx as any).xlsx || jsonAsXlsx;

  let data: IJsonSheet[] = [
    {
      sheet: "Roster",
      columns: [
        {
          label: "First Name",
          value: "firstName",
        },
        {
          label: "Last Name",
          value: "lastName",
        },
        {
          label: "Email",
          value: "email",
        },
        {
          label: "Tenant/Dept",
          value: "tenant",
        },
        {
          label: "Role",
          value: "role",
        },
        {
          label: "Floor",
          value: "floor",
        },
        {
          label: "Phone",
          value: "phone",
        },
        {
          label: "Date Registered",
          value: "dateRegistered",
        },
      ],
      content: [] as IContent[],
    },
  ];

  reportResults.value.map((result) => {
    data[0].content.push({
      firstName: result.firstName,
      lastName: result.lastName,
      email: result.loginEmail,
      tenant: result.tenantName,
      role: result.roleAlias,
      floor: result.primaryFloor,
      phone: formatPhoneNumber(result.primaryPhone),
      dateRegistered: formatDate(result.dateRegistered),
    });
  });

  xlsx(data, {
    fileName:
      "BuildingRoster_" +
      property.value.name.replace(" ", "_") +
      "_" +
      new Date(Date.now()).toLocaleDateString("en-US", {
        month: "numeric",
        day: "numeric",
        year: "numeric",
      }),
  });
}

// #region Helpers

var getSiteRoleById = (siteRoleId: number) =>
  property.value?.siteRoles?.find((r) => r.siteRoleId === siteRoleId);

var getSiteRoleByName = (roleName: string) =>
  property.value?.siteRoles?.find((r) => r.roleAlias === roleName);

// #endregion Helpers
</script>
